import { useCallback, useEffect, useRef } from 'react'

/**
 * Pointer-Geste (Maus/Touch/Stift), portiert aus den Prototypen n1/n3.
 * - axis: 'x' | 'y' – Richtung der Geste
 * - accept(d): darf die Geste in Richtung d (px, mit Vorzeichen) starten?
 * - start/move(d)/end(d, v, cancelled): Fingerverfolgung; v = Geschwindigkeit in px/ms
 * Gibt eine Ref-Callback-Funktion zurück:  <div ref={bind} />
 * Nach einer Wischgeste wird der folgende Klick unterdrückt.
 */
export interface SwipeOptions {
  axis: 'x' | 'y'
  accept?: (d: number) => boolean
  start?: () => void
  move: (d: number, e: PointerEvent) => void
  end: (d: number, v: number, cancelled: boolean) => void
  filter?: (e: PointerEvent) => boolean
}

export function useSwipe(options: SwipeOptions) {
  const opts = useRef(options)
  opts.current = options
  const cleanup = useRef<(() => void) | null>(null)

  const bind = useCallback((el: HTMLElement | null) => {
    cleanup.current?.()
    cleanup.current = null
    if (!el) return

    let id: number | null = null
    let sx = 0
    let sy = 0
    let drag = false
    let samples: { t: number; p: number }[] = []

    const stop = () => {
      id = null
      drag = false
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onUp)
    }

    function onMove(e: PointerEvent) {
      if (e.pointerId !== id) return
      const o = opts.current
      const p = o.axis === 'x' ? e.clientX : e.clientY
      const q = o.axis === 'x' ? e.clientY : e.clientX
      const d = p - sx
      const c = q - sy
      if (!drag) {
        if (Math.abs(d) > 8 && Math.abs(d) > Math.abs(c) * 1.2) {
          if (o.accept && !o.accept(d)) { stop(); return }
          drag = true
          try { el!.setPointerCapture(e.pointerId) } catch { /* ignorieren */ }
          o.start?.()
        } else if (Math.abs(c) > 12 && Math.abs(c) > Math.abs(d)) {
          stop(); return
        } else return
      }
      samples.push({ t: e.timeStamp, p })
      while (samples.length > 1 && e.timeStamp - samples[0].t > 100) samples.shift()
      o.move(d, e)
    }

    function onUp(e: PointerEvent) {
      if (e.pointerId !== id) return
      const o = opts.current
      const p = o.axis === 'x' ? e.clientX : e.clientY
      if (drag) {
        // Klick nach der Geste unterdrücken
        const block = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); window.removeEventListener('click', block, true) }
        window.addEventListener('click', block, true)
        setTimeout(() => window.removeEventListener('click', block, true), 80)
        let v = 0
        const a = samples[0]
        const z = samples[samples.length - 1]
        if (samples.length > 1 && e.timeStamp - z.t < 80 && z.t > a.t) v = (z.p - a.p) / (z.t - a.t)
        stop()
        o.end(p - sx, v, e.type === 'pointercancel')
      } else stop()
    }

    const onDown = (e: PointerEvent) => {
      const o = opts.current
      if (e.pointerType === 'mouse' && e.button !== 0) return
      if (o.filter && !o.filter(e)) return
      stop()
      id = e.pointerId
      sx = o.axis === 'x' ? e.clientX : e.clientY
      sy = o.axis === 'x' ? e.clientY : e.clientX
      samples = [{ t: e.timeStamp, p: sx }]
      drag = false
      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
      document.addEventListener('pointercancel', onUp)
    }

    el.addEventListener('pointerdown', onDown)
    cleanup.current = () => { stop(); el.removeEventListener('pointerdown', onDown) }
  }, [])

  useEffect(() => () => { cleanup.current?.() }, [])
  return bind
}

export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v))
